import React, { useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { pdf } from '@react-pdf/renderer';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import { useMemberInfo } from '../../../shared/hooks/useApi/useMemberInfo';
import { useCareer } from '../../../shared/hooks/useApi/useCareer';
import { useAboutPageSettingState } from '../../../shared/hooks/useApi/useAboutPageSetting';
import { useCanShowAboutPageSetting } from '../../../shared/hooks/useAboutPageSettingVisibility';
import DocumentHorizontalCV from '../DocumentHorizontalCV';
import DocumentVerticalCV from '../DocumentVerticalCV';
import { AboutContentMode, PdfLayout } from '../../../shared/enum/EnumRepository';
import belowIcon from '../../../asset/icon/below.png';

const AboutCvPdfModalMobile: React.FC = () => {
  const { clearModal } = useModalStore();
  const { memberInfo } = useMemberInfo();
  const { careerList } = useCareer();
  const { aboutPageSetting } = useAboutPageSettingState();
  const canShowAboutPageSetting = useCanShowAboutPageSetting();
  const [contentMode, setContentMode] = useState<AboutContentMode>(AboutContentMode.Orig);
  const [pdfLayout, setPdfLayout] = useState<PdfLayout>(PdfLayout.Vertical);

  if (!memberInfo) return null;

  const handleGenerate = async () => {
    const docProps = {
      memberInfo,
      careerList,
      careerSectionOrder: canShowAboutPageSetting
        ? aboutPageSetting?.careerSectionOrder
        : undefined,
      contentMode,
    };
    const doc =
      pdfLayout === PdfLayout.Horizontal ? (
        <DocumentHorizontalCV {...docProps} />
      ) : (
        <DocumentVerticalCV {...docProps} />
      );
    const blob = await pdf(doc).toBlob();

    const cvFileStem =
      contentMode === AboutContentMode.ENG
        ? (memberInfo.nameEn ?? memberInfo.name)
        : memberInfo.name;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      pdfLayout === PdfLayout.Horizontal
        ? `CV_${cvFileStem}_landscape.pdf`
        : `CV_${cvFileStem}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
    clearModal(400);
  };

  const handleClose = () => {
    clearModal(400);
  };

  return (
    <Root>
      <TitleWrapper>
        <ModalTitle>{getModalMessage('ABOUT_PDF_OPTIONS_TITLE')}</ModalTitle>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
        />
      </TitleWrapper>

      <Content>
        <OptionSection>
          <OptionLabel>{getModalMessage('EXPORT_LANGUAGE_LABEL')}</OptionLabel>
          <RadioGroup>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvVersionMobile"
                checked={contentMode === AboutContentMode.Orig}
                onChange={() => setContentMode(AboutContentMode.Orig)}
              />
              {getModalMessage('EXPORT_LANGUAGE_ORIGINAL')}
            </CheckboxComp>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvVersionMobile"
                checked={contentMode === AboutContentMode.ENG}
                onChange={() => setContentMode(AboutContentMode.ENG)}
              />
              {getModalMessage('EXPORT_LANGUAGE_ENGLISH')}
            </CheckboxComp>
          </RadioGroup>
        </OptionSection>

        <OptionSection>
          <OptionLabel>{getModalMessage('ABOUT_PDF_LAYOUT_LABEL')}</OptionLabel>
          <RadioGroup>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvPdfLayoutMobile"
                checked={pdfLayout === PdfLayout.Vertical}
                onChange={() => setPdfLayout(PdfLayout.Vertical)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_PORTRAIT')}
            </CheckboxComp>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvPdfLayoutMobile"
                checked={pdfLayout === PdfLayout.Horizontal}
                onChange={() => setPdfLayout(PdfLayout.Horizontal)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_LANDSCAPE')}
            </CheckboxComp>
          </RadioGroup>
        </OptionSection>
      </Content>
      <HeadlessBtn
        value={getBtnName('GENERATE')}
        handleClick={handleGenerate}
        StyledBtn={SubmitButton}
      />
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  max-height: 95vh;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const ModalTitle = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  padding-bottom: 20px;
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionLabel = styled.div`
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-bottom: 1vh;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const Checkbox = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 12px;
  height: 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  margin-right: 10px;
  outline: none;
  transition: border 0.2s, background 0.2s;
  position: relative;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: -3px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.color_Gray_02};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

export default AboutCvPdfModalMobile;
